import Tippy from "@tippyjs/react";
import { type ReactElement, useState } from "react";
import "./SuggestionPopover.css";
import {
  SuggestionPopoverContent,
  type SuggestionPopoverVariant,
} from "./SuggestionPopoverContent";

export interface SuggestionData {
  id: string;
  reason: string;
  reasonDetails?: string | null;
  confidence?: number | null;
  relatedSegmentID?: string | null;
  seen?: boolean | null;
}

interface SuggestionPopoverProps {
  /**
   * Suggestion data to display
   */
  suggestion: SuggestionData;

  /**
   * Layout variant for the suggestion popover content.
   * - "default": Vertical layout with full details (reason, explanation, confidence)
   * - "minimal": Horizontal compact layout with just reason and actions
   * Defaults to "default".
   */
  variant?: SuggestionPopoverVariant;

  /**
   * Badge label to display (e.g., "Suggested Delete", "Suggested Removal")
   * Defaults to "Suggested Delete".
   */
  badgeLabel?: string;

  /**
   * Callback when suggestion is accepted
   */
  onAccept: () => void;

  /**
   * Callback when suggestion is rejected
   */
  onReject: () => void;

  /**
   * Callback when "View source entry" link is clicked.
   * Only rendered when provided (typically for duplicate suggestions with a related segment).
   */
  onViewRelatedSegment?: () => void;

  /**
   * Callback to navigate to a segment by its logical segment ID.
   * When provided, UUIDs in reasonDetails are rendered as clickable links.
   */
  onNavigateToSegment?: (segmentId: string) => void;

  /**
   * Trigger element (typically a Tag)
   */
  children: ReactElement;
}

/**
 * Popover component for displaying AI suggestions with Accept/Reject actions.
 *
 * Uses Tippy.js for positioning and the shared SuggestionPopoverContent
 * for consistent UI across the application.
 *
 * When Accept is clicked, the parent should handle the acceptance logic
 * (e.g., mark for deletion, update suggestion status).
 *
 * When Reject is clicked, the parent should handle the rejection logic
 * (e.g., update suggestion status, remove suggestion UI).
 */
export const SuggestionPopover: React.FC<SuggestionPopoverProps> = ({
  suggestion,
  variant = "default",
  badgeLabel = "Suggested Delete",
  onAccept,
  onReject,
  onViewRelatedSegment,
  onNavigateToSegment,
  children,
}) => {
  const [isVisible, setIsVisible] = useState(false);

  const handleAccept = () => {
    setIsVisible(false);
    onAccept();
  };

  const handleReject = () => {
    setIsVisible(false);
    onReject();
  };

  const handleViewRelatedSegment = onViewRelatedSegment
    ? () => {
        setIsVisible(false);
        onViewRelatedSegment();
      }
    : undefined;

  const popoverContent = (
    <div className="indigo-suggestion-popover">
      <SuggestionPopoverContent
        variant={variant}
        badgeLabel={badgeLabel}
        reason={suggestion.reason}
        reasonDetails={suggestion.reasonDetails}
        relatedSegmentID={suggestion.relatedSegmentID}
        confidence={suggestion.confidence}
        onAccept={handleAccept}
        onReject={handleReject}
        onViewRelatedSegment={handleViewRelatedSegment}
        onNavigateToSegment={onNavigateToSegment}
      />
    </div>
  );

  return (
    <Tippy
      content={popoverContent}
      interactive
      placement="bottom-start"
      offset={[0, 4]}
      visible={isVisible}
      onClickOutside={() => setIsVisible(false)}
      appendTo={() => document.body}
      className="indigo-suggestion-tippy"
      maxWidth="600px"
    >
      {/* Wrap in span to provide ref target for Tippy and handle click */}
      <span
        onClick={(e) => {
          e.stopPropagation();
          setIsVisible(!isVisible);
        }}
        style={{ display: "inline-block" }}
      >
        {children}
      </span>
    </Tippy>
  );
};
